import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllPartyPlatters,
  getSinglePartyPlatter,
  createPartyPlatter,
  updatePartyPlatter,
  deletePartyPlatter,
} from "@/api/new-api/staff-management/menu-manage/special-menu/party-platters";

// Thunks

export const fetchAllPartyPlatters = createAsyncThunk(
  "partyPlatters/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllPartyPlatters(restaurant_id);
    if (res.status === "success") return res.data.party_platters;
    return rejectWithValue(res.message);
  }
);

export const fetchSinglePartyPlatter = createAsyncThunk(
  "partyPlatters/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSinglePartyPlatter(id);
    if (res.status === "success") return res.data.party_platter;
    return rejectWithValue(res.message);
  }
);

export const createNewPartyPlatter = createAsyncThunk(
  "partyPlatters/create",
  async (data, { rejectWithValue }) => {
    const res = await createPartyPlatter(data);
    if (res.status === "success") return res.data.party_platter;
    return rejectWithValue(res.message);
  }
);

export const updateExistingPartyPlatter = createAsyncThunk(
  "partyPlatters/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updatePartyPlatter(id, data);
    if (res.status === "success") return res.data.party_platter;
    return rejectWithValue(res.message);
  }
);

export const deletePartyPlatterById = createAsyncThunk(
  "partyPlatters/delete",
  async (id, { rejectWithValue }) => {
    const res = await deletePartyPlatter(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State
const initialState = {
  partyPlatters: [],
  singlePartyPlatter: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice
const partyPlatterSlice = createSlice({
  name: "partyPlatters",
  initialState,
  reducers: {
    clearPartyPlatterMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllPartyPlatters.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllPartyPlatters.fulfilled, (state, action) => {
        state.getLoading = false;
        state.partyPlatters = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllPartyPlatters.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSinglePartyPlatter.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSinglePartyPlatter.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singlePartyPlatter = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSinglePartyPlatter.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewPartyPlatter.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewPartyPlatter.fulfilled, (state, action) => {
        state.createLoading = false;
        state.partyPlatters.push(action.payload);
        state.success = "Party Platter created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewPartyPlatter.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingPartyPlatter.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingPartyPlatter.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.partyPlatters = state.partyPlatters.map((platter) =>
          platter.id === action.payload.id ? action.payload : platter
        );
        state.success = "Party Platter updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingPartyPlatter.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deletePartyPlatterById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deletePartyPlatterById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.partyPlatters = state.partyPlatters.filter(
          (platter) => platter.id !== action.payload
        );
        state.success = "Party Platter deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deletePartyPlatterById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearPartyPlatterMessages } = partyPlatterSlice.actions;
export default partyPlatterSlice.reducer;
